import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardService } from "../services/api";
import { formatCurrency, toPersianNumbers } from "../utils/numbers";
import {
  Wallet,
  FileText,
  CheckCircle,
  Calendar,
  Clock,
  Banknote,
  AlertCircle,
  AlertTriangle,
  Coins,
  Users,
  Search,
  ArrowLeft,
  ShieldCheck,
  TrendingUp,
  CheckCircle2,
  Sparkles,
} from "lucide-react";

interface MemberDeposit {
  id: number;
  firstName: string;
  lastName: string;
  role: string;
  nationalCode?: string;
  mobile?: string;
  totalAmount: number;
  count: number;
  lastDate?: string;
  penaltiesAmount?: number;
  accountValue?: number;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [memberRoleFilter, setMemberRoleFilter] = useState<
    "all" | "admin" | "investor"
  >("all");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await dashboardService.getStats();
      setStats(data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = user?.role === "admin";

  const members: MemberDeposit[] = useMemo(() => {
    return stats?.membersDeposits || [];
  }, [stats]);

  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      const matchesRole =
        memberRoleFilter === "all" ? true : m.role === memberRoleFilter;
      const q = memberSearchQuery.trim().toLowerCase();
      const fullName = `${m.firstName || ""} ${m.lastName || ""}`.toLowerCase();
      const national = (m.nationalCode || "").toLowerCase();
      const mobile = (m.mobile || "").toLowerCase();
      const matchesSearch =
        !q ||
        fullName.includes(q) ||
        national.includes(q) ||
        mobile.includes(q);
      return matchesRole && matchesSearch;
    });
  }, [members, memberSearchQuery, memberRoleFilter]);

  const adminMembersCount = useMemo(
    () => members.filter((m) => m.role === "admin").length,
    [members],
  );
  const investorMembersCount = useMemo(
    () => members.filter((m) => m.role === "investor").length,
    [members],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <div className="w-8 h-8 border-4 border-persian-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            داشبورد سامانه نهام
          </h1>
          <p className="text-slate-600 dark:text-slate-300">
            خوش آمدید، {user?.first_name} {user?.last_name}
            <span className="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-persian-50 text-persian-700 dark:bg-persian-900/30 dark:text-persian-300 border border-persian-200 dark:border-persian-800">
              {user?.role === "admin"
                ? "مدیر صندوق"
                : user?.role === "investor"
                  ? "سرمایه‌گذار"
                  : "کاربر سامانه"}
            </span>
          </p>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin ? (
            <>
              <button
                onClick={() => navigate("/investments")}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-persian-600 hover:bg-persian-700 text-white text-xs md:text-sm font-medium transition shadow-sm"
              >
                <TrendingUp className="w-4 h-4" />
                <span>سرمایه‌گذاری</span>
              </button>
              <button
                onClick={() => navigate("/weekly-penalties")}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs md:text-sm font-medium transition shadow-sm"
              >
                <Coins className="w-4 h-4" />
                <span>جریمه‌های هفتگی</span>
              </button>
            </>
          ) : (
            user?.role === "investor" && (
              <button
                onClick={() => navigate("/investments")}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-persian-600 hover:bg-persian-700 text-white text-xs md:text-sm font-medium transition shadow-sm"
              >
                <Coins className="w-4 h-4" />
                <span>مشاهده سوابق سرمایه‌گذاری</span>
                <ArrowLeft className="w-4 h-4" />
              </button>
            )
          )}
        </div>
      </div>

      {isAdmin ? (
        <>
          {/* Admin Stats Grid - Interactive Clickable Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {/* 1. موجودی صندوق -> /balance */}
            <div
              onClick={() => navigate("/balance")}
              role="button"
              tabIndex={0}
              className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Wallet className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>مشاهده صندوق</span>
                    <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                  </span>
                </div>
                <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">
                  موجودی صندوق
                </h3>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
                  {formatCurrency(stats?.balance || 0)}
                </p>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-700/50 mt-2">
                موجودی نقدینگی فعلی ریالی
              </p>
            </div>

            {/* 2. واریزی شخصی مدیر تا الان -> /investments */}
            <div
              onClick={() => navigate("/investments")}
              role="button"
              tabIndex={0}
              className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-emerald-200 dark:border-emerald-800/40 bg-gradient-to-br from-white to-emerald-50/20 dark:from-slate-800 dark:to-emerald-950/20 hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Coins className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>سرمایه‌گذاری</span>
                    <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                  </span>
                </div>
                <div>
                  <h3 className="text-slate-700 dark:text-slate-300 text-sm font-semibold">
                    مجموع واریزی شما تا الان
                  </h3>
                  <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                    سرمایه‌گذاری شخصی مدیر
                  </span>
                </div>
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400 mt-1 mb-1">
                  {formatCurrency(stats?.myDeposits?.totalAmount || 0)}
                </p>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2 pt-2 border-t border-emerald-100 dark:border-emerald-900/30 mt-2">
                <span>
                  {toPersianNumbers(stats?.myDeposits?.count || 0)} مرحله واریز
                </span>
                {stats?.myDeposits?.lastDate && (
                  <>
                    <span>•</span>
                    <span>
                      آخرین: {toPersianNumbers(stats.myDeposits.lastDate)}
                    </span>
                  </>
                )}
              </p>
            </div>

            {/* 3. کل واریزی‌های سرمایه‌گذاران و مدیران -> /investments */}
            <div
              onClick={() => navigate("/investments")}
              role="button"
              tabIndex={0}
              className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-indigo-200 dark:border-indigo-800/40 bg-gradient-to-br from-white to-indigo-50/20 dark:from-slate-800 dark:to-indigo-950/20 hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Users className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>سرمایه‌گذاری</span>
                    <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                  </span>
                </div>
                <div>
                  <h3 className="text-slate-700 dark:text-slate-300 text-sm font-semibold">
                    کل واریزی اعضا و مدیران
                  </h3>
                  <span className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium">
                    مجموع سرمایه جذب‌شده
                  </span>
                </div>
                <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-400 mt-1 mb-1">
                  {formatCurrency(
                    stats?.totalMembersDeposits?.totalAmount || 0,
                  )}
                </p>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-indigo-100 dark:border-indigo-900/30 mt-2">
                {toPersianNumbers(stats?.totalMembersDeposits?.count || 0)} عضو
                سرمایه‌گذار و مدیر
              </p>
            </div>

            {/* 4. ارزش هر اکانت -> /investments */}
            <div
              onClick={() => navigate("/investments")}
              role="button"
              tabIndex={0}
              className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-teal-200 dark:border-teal-800/40 bg-gradient-to-br from-white to-teal-50/20 dark:from-slate-800 dark:to-teal-950/20 hover:border-teal-400 dark:hover:border-teal-500 hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 dark:bg-teal-900/40 dark:text-teal-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-medium text-teal-600 dark:text-teal-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>سرمایه‌گذاری</span>
                    <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                  </span>
                </div>
                <div>
                  <h3 className="text-slate-700 dark:text-slate-300 text-sm font-semibold">
                    ارزش هر اکانت
                  </h3>
                  <span className="text-[11px] text-teal-600 dark:text-teal-400 font-medium">
                    سهم از کل دارایی خالص صندوق
                  </span>
                </div>
                <p className="text-2xl font-bold text-teal-700 dark:text-teal-400 mt-1 mb-1">
                  {formatCurrency(stats?.accountValueStats?.accountValue || 0)}
                </p>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-teal-100 dark:border-teal-900/30 mt-2 flex items-center justify-between">
                <span>سهم سود و ارزش افزوده:</span>
                <span className="font-bold font-bold text-teal-700 dark:text-teal-300">
                  +
                  {formatCurrency(
                    stats?.accountValueStats?.valueAddedPerInvestor ||
                      stats?.accountValueStats?.netProfitPerMember ||
                      0,
                  )}
                </span>
              </div>
            </div>

            {/* 5. کادر جریمه‌های شما / صندوق تا این لحظه -> /weekly-penalties */}
            <div
              onClick={() => navigate("/weekly-penalties")}
              role="button"
              tabIndex={0}
              className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-amber-200 dark:border-amber-800/40 bg-gradient-to-br from-white to-amber-50/20 dark:from-slate-800 dark:to-amber-950/20 hover:border-amber-400 dark:hover:border-amber-500 hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>جریمه‌های هفتگی</span>
                    <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                  </span>
                </div>
                <div>
                  <h3 className="text-slate-700 dark:text-slate-300 text-sm font-semibold">
                    مجموع جریمه‌های شما تا الان
                  </h3>
                  <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
                    جریمه‌های معوق شخصی
                  </span>
                </div>
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-400 mt-1 mb-1">
                  {formatCurrency(stats?.myPenalties?.totalAmount || 0)}
                </p>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-amber-100 dark:border-amber-900/30 mt-2 flex items-center justify-between">
                <span>کل جریمه‌های ثبت‌شده صندوق:</span>
                <span className="font-bold font-bold text-slate-700 dark:text-slate-300">
                  {formatCurrency(
                    stats?.totalSystemPenalties?.totalAmount || 0,
                  )}
                </span>
              </div>
            </div>

            {/* 5. اقساط پرداخت‌نشده تا امروز -> /installments */}
            <div
              onClick={() => navigate("/installments")}
              role="button"
              tabIndex={0}
              className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Banknote className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-medium text-purple-600 dark:text-purple-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>تایید پرداخت اقساط</span>
                    <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                  </span>
                </div>
                <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">
                  اقساط پرداخت‌نشده تا امروز
                </h3>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
                  {toPersianNumbers(stats?.todayInstallments?.count || 0)}{" "}
                  <span className="text-sm font-normal text-slate-500">
                    مورد
                  </span>
                </p>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-700/50 mt-2">
                مجموع مبلغ:{" "}
                {formatCurrency(stats?.todayInstallments?.totalAmount || 0)}
              </p>
            </div>

            {/* 6. وام‌های در انتظار بررسی -> /loan-reservations */}
            <div
              onClick={() => navigate("/loan-reservations")}
              role="button"
              tabIndex={0}
              className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 hover:border-amber-400 dark:hover:border-amber-500 hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Clock className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>تایید ثبت اولیه</span>
                    <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                  </span>
                </div>
                <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">
                  وام‌های در انتظار بررسی
                </h3>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
                  {toPersianNumbers(stats?.pendingLoans?.count || 0)}{" "}
                  <span className="text-sm font-normal text-slate-500">
                    مورد
                  </span>
                </p>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-700/50 mt-2">
                مجموع مبلغ:{" "}
                {formatCurrency(stats?.pendingLoans?.totalAmount || 0)}
              </p>
            </div>

            {/* 7. منتظر پرداخت کارمزد -> /loan-fee-receipt */}
            <div
              onClick={() => navigate("/loan-fee-receipt")}
              role="button"
              tabIndex={0}
              className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 hover:border-teal-400 dark:hover:border-teal-500 hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <FileText className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-medium text-teal-600 dark:text-teal-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>دریافت کارمزد اولیه</span>
                    <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                  </span>
                </div>
                <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">
                  منتظر پرداخت کارمزد
                </h3>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
                  {toPersianNumbers(stats?.approvedLoans?.count || 0)}{" "}
                  <span className="text-sm font-normal text-slate-500">
                    مورد
                  </span>
                </p>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-700/50 mt-2">
                مجموع مبلغ وام:{" "}
                {formatCurrency(stats?.approvedLoans?.totalAmount || 0)}
              </p>
            </div>

            {/* 8. وام‌های منتظر پرداخت -> /loan-payments */}
            <div
              onClick={() => navigate("/loan-payments")}
              role="button"
              tabIndex={0}
              className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 hover:border-green-400 dark:hover:border-green-500 hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-medium text-green-600 dark:text-green-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>پرداخت وام</span>
                    <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                  </span>
                </div>
                <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">
                  وام‌های منتظر پرداخت
                </h3>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
                  {toPersianNumbers(stats?.feePaidLoans?.count || 0)}{" "}
                  <span className="text-sm font-normal text-slate-500">
                    مورد
                  </span>
                </p>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-700/50 mt-2">
                مجموع مبلغ:{" "}
                {formatCurrency(stats?.feePaidLoans?.totalAmount || 0)}
              </p>
            </div>

            {/* 9. اقساط مانده این ماه -> /loan-history */}
            <div
              onClick={() => navigate("/loan-history")}
              role="button"
              tabIndex={0}
              className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-md hover:-translate-y-1 transition-all duration-200 cursor-pointer group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <span className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>تاریخچه وام‌ها</span>
                    <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                  </span>
                </div>
                <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">
                  اقساط مانده این ماه
                </h3>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
                  {toPersianNumbers(stats?.monthInstallments?.count || 0)}{" "}
                  <span className="text-sm font-normal text-slate-500">
                    مورد
                  </span>
                </p>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-700/50 mt-2">
                مجموع مبلغ:{" "}
                {formatCurrency(stats?.monthInstallments?.totalAmount || 0)}
              </p>
            </div>
          </div>

          {/* کادر اختصاصی وضعیت واریزی تک‌تک سرمایه‌گذاران و مدیران */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            {/* Header of Section */}
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                      کادر وضعیت واریزی سرمایه‌گذاران و مدیران
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      نمایش میزان واریزی و وضعیت جریمه هر سرمایه‌گذار و مدیر تا
                      این لحظه به همراه تعداد مراحل و آخرین تاریخ
                    </p>
                  </div>
                </div>

                {/* Filters & Search */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* Role Filter Chips */}
                  <div className="flex items-center bg-slate-100 dark:bg-slate-700/60 p-1 rounded-xl text-xs font-medium">
                    <button
                      onClick={() => setMemberRoleFilter("all")}
                      className={`px-3 py-1.5 rounded-lg transition ${
                        memberRoleFilter === "all"
                          ? "bg-white dark:bg-slate-800 text-persian-600 dark:text-persian-400 shadow-sm"
                          : "text-slate-600 dark:text-slate-300 hover:text-slate-900"
                      }`}
                    >
                      همه ({toPersianNumbers(members.length)})
                    </button>
                    <button
                      onClick={() => setMemberRoleFilter("admin")}
                      className={`px-3 py-1.5 rounded-lg transition ${
                        memberRoleFilter === "admin"
                          ? "bg-white dark:bg-slate-800 text-persian-600 dark:text-persian-400 shadow-sm"
                          : "text-slate-600 dark:text-slate-300 hover:text-slate-900"
                      }`}
                    >
                      مدیران ({toPersianNumbers(adminMembersCount)})
                    </button>
                    <button
                      onClick={() => setMemberRoleFilter("investor")}
                      className={`px-3 py-1.5 rounded-lg transition ${
                        memberRoleFilter === "investor"
                          ? "bg-white dark:bg-slate-800 text-persian-600 dark:text-persian-400 shadow-sm"
                          : "text-slate-600 dark:text-slate-300 hover:text-slate-900"
                      }`}
                    >
                      سرمایه‌گذاران ({toPersianNumbers(investorMembersCount)})
                    </button>
                  </div>

                  {/* Search Input */}
                  <div className="relative min-w-[200px]">
                    <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={memberSearchQuery}
                      onChange={(e) => setMemberSearchQuery(e.target.value)}
                      placeholder="جستجوی نام یا کد ملی..."
                      className="w-full pl-3 pr-9 py-1.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 text-slate-900 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Table of Members */}
            <div className="overflow-x-auto">
              <table className="w-full text-right border-collapse">
                <thead>
                  <tr className="bg-slate-50/75 dark:bg-slate-700/30 text-xs font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                    <th className="py-3 px-4">ردیف</th>
                    <th className="py-3 px-4">نام و نام خانوادگی</th>
                    <th className="py-3 px-4">نقش</th>
                    <th className="py-3 px-4 text-center">تعداد واریزی</th>
                    <th className="py-3 px-4 text-center">آخرین تاریخ واریز</th>
                    <th className="py-3 px-4 text-center">جریمه‌های هفتگی</th>
                    <th className="py-3 px-4 text-left">
                      مجموع واریزی تا الان
                    </th>
                    <th className="py-3 px-4 text-left">ارزش هر اکانت</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50 text-sm">
                  {filteredMembers.length > 0 ? (
                    filteredMembers.map((member, index) => {
                      const isCurrentUser = member.id === user?.id;
                      const memberAccountValue =
                        member.accountValue ||
                        Number(member.totalAmount || 0) +
                          Number(
                            stats?.accountValueStats?.valueAddedPerInvestor ||
                              0,
                          );
                      return (
                        <tr
                          key={member.id}
                          className={`hover:bg-slate-50/80 dark:hover:bg-slate-700/20 transition ${
                            isCurrentUser
                              ? "bg-emerald-50/30 dark:bg-emerald-950/20 font-medium"
                              : ""
                          }`}
                        >
                          <td className="py-3 px-4 text-xs text-slate-400">
                            {toPersianNumbers(index + 1)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900 dark:text-white">
                                {member.firstName} {member.lastName}
                              </span>
                              {isCurrentUser && (
                                <span className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 px-2 py-0.5 rounded-full font-bold">
                                  شما
                                </span>
                              )}
                            </div>
                            {member.nationalCode && (
                              <span className="text-[11px] text-slate-400 font-bold block">
                                کد ملی: {toPersianNumbers(member.nationalCode)}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {member.role === "admin" ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                <ShieldCheck className="w-3 h-3" />
                                مدیر
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                                <Coins className="w-3 h-3" />
                                سرمایه‌گذار
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center text-slate-700 dark:text-slate-300">
                            <span className="inline-block px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 font-medium text-xs">
                              {toPersianNumbers(member.count)} مرحله
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-xs text-slate-600 dark:text-slate-300 font-bold">
                            {member.lastDate
                              ? toPersianNumbers(member.lastDate)
                              : "—"}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {Number(member.penaltiesAmount || 0) > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 font-bold text-xs font-semibold">
                                {formatCurrency(member.penaltiesAmount || 0)}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400 dark:text-slate-500 font-bold">
                                ۰ ریال
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-left">
                            <div className="font-bold text-emerald-600 dark:text-emerald-400 font-bold text-base">
                              {formatCurrency(member.totalAmount)}
                            </div>
                            <div className="text-[11px] text-slate-400 font-bold">
                              معادل{" "}
                              {formatCurrency(
                                Math.floor(member.totalAmount / 10),
                              ).replace("ریال", "تومان")}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-left">
                            <div className="font-bold text-teal-600 dark:text-teal-400 font-bold text-base">
                              {formatCurrency(memberAccountValue)}
                            </div>
                            <div className="text-[11px] text-teal-600/70 dark:text-teal-400/70 font-bold">
                              معادل{" "}
                              {formatCurrency(
                                Math.floor(memberAccountValue / 10),
                              ).replace("ریال", "تومان")}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={8}
                        className="py-8 text-center text-slate-500 dark:text-slate-400 text-sm"
                      >
                        هیچ سرمایه‌گذار یا مدیری با این مشخصات یافت نشد.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer Summary */}
            <div className="p-4 bg-slate-50 dark:bg-slate-700/20 border-t border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600 dark:text-slate-400">
              <div className="flex items-center gap-2">
                <span>
                  تعداد اعضا:{" "}
                  <strong>{toPersianNumbers(filteredMembers.length)}</strong>{" "}
                  نفر
                </span>
                <span>•</span>
                <span>
                  مجموع واریزی این لیست:{" "}
                  <strong className="text-emerald-600 dark:text-emerald-400 font-bold font-bold text-sm">
                    {formatCurrency(
                      filteredMembers.reduce(
                        (sum, m) => sum + Number(m.totalAmount || 0),
                        0,
                      ),
                    )}
                  </strong>
                </span>
              </div>
              <button
                onClick={() => navigate("/investments")}
                className="text-persian-600 dark:text-persian-400 hover:underline font-medium flex items-center gap-1"
              >
                <span>مشاهده گزارش تفصیلی و ثبت واریزی‌ها</span>
                <ArrowLeft className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </>
      ) : (
        /* User & Investor Dashboard */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* کادر ارزش هر اکانت (ویژه سرمایه‌گذاران) */}
          {user?.role === "investor" && (
            <div
              onClick={() => navigate("/investments")}
              className="col-span-1 md:col-span-2 lg:col-span-3 bg-gradient-to-br from-amber-500/10 via-emerald-500/10 to-teal-500/15 dark:from-amber-950/25 dark:via-emerald-950/25 dark:to-slate-800 rounded-2xl p-6 md:p-8 border-2 border-emerald-400/40 dark:border-emerald-600/40 shadow-sm hover:shadow-md transition-all cursor-pointer group"
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-amber-500 to-emerald-500 text-white flex items-center justify-center shadow-md group-hover:scale-105 transition-transform shrink-0">
                    <Sparkles className="w-7 h-7" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">
                        ارزش هر اکانت
                      </h2>
                      <span className="text-xs bg-amber-500/10 dark:bg-amber-400/20 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700 px-2.5 py-0.5 rounded-full font-bold">
                        سهم دارایی صندوق
                      </span>
                    </div>
                    <p className="text-xs md:text-sm text-slate-600 dark:text-slate-300 mt-1.5 leading-relaxed">
                      مجموع تمام واریزی‌ها، سودها، کارمزدها و جریمه‌ها منهای
                      هزینه‌ها تقسیم بر تعداد اعضا (ادمین‌ها و سرمایه‌گذاران)
                    </p>
                  </div>
                </div>

                <div className="text-right lg:text-left border-t lg:border-t-0 pt-3 lg:pt-0 border-emerald-200/60 dark:border-slate-700">
                  <div className="text-3xl md:text-4xl font-extrabold text-emerald-700 dark:text-emerald-400 font-bold">
                    {formatCurrency(
                      stats?.accountValueStats?.accountValue || 0,
                    )}
                  </div>
                  <div className="text-xs md:text-sm text-slate-500 dark:text-slate-400 font-bold mt-1">
                    معادل{" "}
                    {formatCurrency(
                      Math.floor(
                        (stats?.accountValueStats?.accountValue || 0) / 10,
                      ),
                    ).replace("ریال", "تومان")}
                  </div>
                </div>
              </div>

              {/* Breakdown Grid / Pills */}
              <div className="mt-6 pt-5 border-t border-emerald-200/70 dark:border-emerald-900/40 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                <div className="bg-white/80 dark:bg-slate-800/80 p-3 rounded-xl border border-emerald-100 dark:border-emerald-900/40">
                  <span className="text-slate-400 block text-[11px] mb-1">
                    مجموع واریزی شما:
                  </span>
                  <strong className="text-emerald-700 dark:text-emerald-300 font-bold text-sm">
                    {formatCurrency(
                      stats?.accountValueStats?.userDeposits ||
                        stats?.myDeposits?.totalAmount ||
                        0,
                    )}
                  </strong>
                </div>

                <div className="bg-white/80 dark:bg-slate-800/80 p-3 rounded-xl border border-emerald-100 dark:border-emerald-900/40">
                  <span className="text-slate-400 block text-[11px] mb-1">
                    سهم سود و ارزش افزوده اکانت:
                  </span>
                  <strong className="text-teal-700 dark:text-teal-300 font-bold text-sm">
                    +
                    {formatCurrency(
                      stats?.accountValueStats?.valueAddedPerInvestor ||
                        stats?.accountValueStats?.netProfitPerMember ||
                        0,
                    )}
                  </strong>
                </div>

                <div className="bg-white/80 dark:bg-slate-800/80 p-3 rounded-xl border border-emerald-100 dark:border-emerald-900/40">
                  <span className="text-slate-400 block text-[11px] mb-1">
                    خالص کل دارایی صندوق:
                  </span>
                  <strong className="text-amber-700 dark:text-amber-300 font-bold text-sm">
                    {formatCurrency(
                      stats?.accountValueStats?.netFundTotal ||
                        stats?.accountValueStats?.valueAddedTotal ||
                        0,
                    )}
                  </strong>
                </div>

                <div className="bg-white/80 dark:bg-slate-800/80 p-3 rounded-xl border border-emerald-100 dark:border-emerald-900/40">
                  <span className="text-slate-400 block text-[11px] mb-1">
                    تعداد ادمین‌ها و سرمایه‌گذاران:
                  </span>
                  <strong className="text-indigo-700 dark:text-indigo-300 font-bold text-sm">
                    {toPersianNumbers(
                      stats?.accountValueStats?.membersCount || 17,
                    )}{" "}
                    نفر
                  </strong>
                </div>
              </div>

              {/* Explanatory Note Footer */}
              <div className="mt-3 pt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                <span>
                  💡 <strong>فرمول محاسبه:</strong> (تمام واریزی‌ها + سودها +
                  کارمزدها + جریمه‌ها - هزینه‌ها) ÷ تعداد ادمین‌ها و
                  سرمایه‌گذاران
                </span>
                <span className="text-emerald-700 dark:text-emerald-400 font-medium flex items-center gap-1 group-hover:translate-x-[-4px] transition-transform">
                  <span>مشاهده جزئیات سرمایه‌گذاری‌ها</span>
                  <ArrowLeft className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          )}

          {/* 1. کادر واریزی اختصاصی برای هر کاربر / سرمایه‌گذار */}
          <div
            onClick={() =>
              user?.role === "investor" && navigate("/investments")
            }
            className={`col-span-1 md:col-span-2 lg:col-span-3 bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-100/60 dark:from-slate-800 dark:via-emerald-950/20 dark:to-slate-800 rounded-2xl p-6 md:p-8 border border-emerald-200 dark:border-emerald-800/60 shadow-sm transition ${
              user?.role === "investor"
                ? "hover:shadow-md cursor-pointer group"
                : ""
            }`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shadow-md">
                  <Coins className="w-7 h-7" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                      مجموع واریزی شما تا الان
                    </h2>
                    {user?.role === "investor" && (
                      <span className="text-xs bg-emerald-600 text-white px-2.5 py-0.5 rounded-full font-medium">
                        سرمایه‌گذار
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                    مجموع کل وجوه واریز شده شما بابت سرمایه‌گذاری در صندوق نهام
                  </p>
                </div>
              </div>

              <div className="text-left sm:text-left border-t sm:border-t-0 pt-3 sm:pt-0 border-emerald-200 dark:border-slate-700">
                <div className="text-3xl font-extrabold text-emerald-700 dark:text-emerald-400 font-bold">
                  {formatCurrency(stats?.myDeposits?.totalAmount || 0)}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 font-bold mt-1">
                  معادل{" "}
                  {formatCurrency(
                    Math.floor((stats?.myDeposits?.totalAmount || 0) / 10),
                  ).replace("ریال", "تومان")}
                </div>
              </div>
            </div>

            {/* Details Pills */}
            <div className="mt-5 pt-4 border-t border-emerald-200 dark:border-emerald-900/30 flex flex-wrap items-center gap-4 text-xs">
              <div className="bg-white/80 dark:bg-slate-800 px-3 py-1.5 rounded-xl border border-emerald-100 dark:border-emerald-900/40 text-slate-700 dark:text-slate-300">
                <span className="text-slate-400 ml-1">تعداد دفعات واریز:</span>
                <strong className="text-slate-900 dark:text-white">
                  {toPersianNumbers(stats?.myDeposits?.count || 0)} مرحله
                </strong>
              </div>

              {stats?.myDeposits?.lastDate && (
                <div className="bg-white/80 dark:bg-slate-800 px-3 py-1.5 rounded-xl border border-emerald-100 dark:border-emerald-900/40 text-slate-700 dark:text-slate-300">
                  <span className="text-slate-400 ml-1">
                    آخرین تاریخ واریزی:
                  </span>
                  <strong className="text-slate-900 dark:text-white font-bold">
                    {toPersianNumbers(stats.myDeposits.lastDate)}
                  </strong>
                </div>
              )}

              {user?.role === "investor" && (
                <div className="bg-emerald-100/60 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 px-3 py-1.5 rounded-xl font-medium flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>عضو فعال سرمایه‌گذار صندوق نهام</span>
                </div>
              )}
            </div>
          </div>

          {/* 2. کادر جریمه‌های کاربر تا این لحظه */}
          <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    Number(stats?.myPenalties?.totalAmount || 0) > 0
                      ? "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400"
                      : "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
                  }`}
                >
                  {Number(stats?.myPenalties?.totalAmount || 0) > 0 ? (
                    <AlertTriangle className="w-6 h-6" />
                  ) : (
                    <CheckCircle2 className="w-6 h-6" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                    مجموع جریمه‌های شما تا این لحظه
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    شامل مجموع مبالغ جریمه‌های دیرکرد واریز هفتگی و جریمه‌های
                    دیرکرد اقساط وام تا این تاریخ
                  </p>
                </div>
              </div>

              <div className="text-left">
                <div
                  className={`text-2xl font-extrabold font-bold ${
                    Number(stats?.myPenalties?.totalAmount || 0) > 0
                      ? "text-red-600 dark:text-red-400"
                      : "text-emerald-600 dark:text-emerald-400"
                  }`}
                >
                  {formatCurrency(stats?.myPenalties?.totalAmount || 0)}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 font-bold mt-0.5">
                  معادل{" "}
                  {formatCurrency(
                    Math.floor((stats?.myPenalties?.totalAmount || 0) / 10),
                  ).replace("ریال", "تومان")}
                </div>
              </div>
            </div>

            {/* Breakdown Pills */}
            <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700/50 flex flex-wrap items-center gap-3 text-xs">
              <div className="px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                <span className="text-slate-400 ml-1">
                  جریمه‌های دیرکرد هفتگی:
                </span>
                <strong className="font-bold text-slate-900 dark:text-white">
                  {formatCurrency(stats?.myPenalties?.weeklyAmount || 0)}
                </strong>
                {Number(stats?.myPenalties?.weeklyCount || 0) > 0 && (
                  <span className="mr-1.5 text-slate-500">
                    ({toPersianNumbers(stats.myPenalties.weeklyCount)} مورد)
                  </span>
                )}
              </div>

              <div className="px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                <span className="text-slate-400 ml-1">
                  جریمه‌های دیرکرد اقساط:
                </span>
                <strong className="font-bold text-slate-900 dark:text-white">
                  {formatCurrency(stats?.myPenalties?.installmentAmount || 0)}
                </strong>
                {Number(stats?.myPenalties?.installmentCount || 0) > 0 && (
                  <span className="mr-1.5 text-slate-500">
                    ({toPersianNumbers(stats.myPenalties.installmentCount)}{" "}
                    مورد)
                  </span>
                )}
              </div>

              {Number(stats?.myPenalties?.totalAmount || 0) === 0 ? (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 font-medium">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>حساب شما فاقد هرگونه جریمه تا این لحظه است.</span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 font-medium">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span>
                    لطفاً نسبت به تسویه جریمه‌های معوق خود اقدام نمایید.
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 3. وام‌های در انتظار بررسی */}
          <div
            onClick={() => navigate("/loans")}
            className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 hover:border-amber-400 dark:hover:border-amber-500 hover:shadow-md transition cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                <Clock className="w-6 h-6" />
              </div>
              <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <span>مشاهده وام‌ها</span>
                <ArrowLeft className="w-3.5 h-3.5" />
              </span>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">
              وام‌های در انتظار بررسی
            </h3>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              {toPersianNumbers(stats?.pendingLoans?.count || 0)}{" "}
              <span className="text-sm font-normal text-slate-500">مورد</span>
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              مبلغ: {formatCurrency(stats?.pendingLoans?.totalAmount || 0)}
            </p>
          </div>

          {/* 4. منتظر پرداخت کارمزد */}
          <div
            onClick={() => navigate("/loans")}
            className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 hover:border-teal-400 dark:hover:border-teal-500 hover:shadow-md transition cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                <FileText className="w-6 h-6" />
              </div>
              <span className="text-[11px] font-medium text-teal-600 dark:text-teal-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <span>مشاهده وام‌ها</span>
                <ArrowLeft className="w-3.5 h-3.5" />
              </span>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">
              منتظر پرداخت کارمزد
            </h3>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              {toPersianNumbers(stats?.approvedLoans?.count || 0)}{" "}
              <span className="text-sm font-normal text-slate-500">مورد</span>
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              مبلغ وام: {formatCurrency(stats?.approvedLoans?.totalAmount || 0)}
            </p>
          </div>

          {/* 5. وام‌های منتظر پرداخت */}
          <div
            onClick={() => navigate("/loans")}
            className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 hover:border-green-400 dark:hover:border-green-500 hover:shadow-md transition cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                <CheckCircle className="w-6 h-6" />
              </div>
              <span className="text-[11px] font-medium text-green-600 dark:text-green-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <span>مشاهده وام‌ها</span>
                <ArrowLeft className="w-3.5 h-3.5" />
              </span>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">
              وام‌های منتظر پرداخت
            </h3>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              {toPersianNumbers(stats?.feePaidLoans?.count || 0)}{" "}
              <span className="text-sm font-normal text-slate-500">مورد</span>
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              مبلغ: {formatCurrency(stats?.feePaidLoans?.totalAmount || 0)}
            </p>
          </div>

          {/* 6. تعداد اقساط باقیمانده */}
          <div
            onClick={() => navigate("/loans")}
            className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md transition cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                <Banknote className="w-6 h-6" />
              </div>
              <span className="text-[11px] font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <span>مشاهده وام‌ها</span>
                <ArrowLeft className="w-3.5 h-3.5" />
              </span>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">
              تعداد اقساط باقیمانده
            </h3>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              {toPersianNumbers(stats?.remainingInstallments?.count || 0)}{" "}
              <span className="text-sm font-normal text-slate-500">قسط</span>
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              مجموع مبلغ:{" "}
              {formatCurrency(stats?.remainingInstallments?.totalAmount || 0)}
            </p>
          </div>

          {/* 7. اقساط پرداخت‌نشده شما (تا امروز) */}
          <div className="col-span-1 md:col-span-2 lg:col-span-3">
            {stats?.todayInstallments && stats.todayInstallments.length > 0 ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                    <h3 className="text-lg font-bold text-red-900 dark:text-red-300">
                      اقساط پرداخت‌نشده شما (تا امروز)
                    </h3>
                  </div>
                  <button
                    onClick={() => navigate("/loans")}
                    className="text-xs text-red-700 dark:text-red-300 hover:underline flex items-center gap-1 font-medium"
                  >
                    <span>پرداخت و مشاهده وام‌ها</span>
                    <ArrowLeft className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="space-y-3">
                  {stats.todayInstallments.map((inst: any, idx: number) => (
                    <div
                      key={idx}
                      className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-red-100 dark:border-red-900/50 flex flex-wrap gap-4 items-center justify-between shadow-sm"
                    >
                      <div>
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          کد وام:
                        </span>
                        <bdi
                          dir="ltr"
                          className="font-medium text-slate-900 dark:text-white mr-2 inline-block font-bold"
                        >
                          {toPersianNumbers(inst.loan_code)}
                        </bdi>
                      </div>
                      <div>
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          کد قسط:
                        </span>
                        <bdi
                          dir="ltr"
                          className="font-medium text-slate-900 dark:text-white mr-2 inline-block font-bold"
                        >
                          {toPersianNumbers(inst.installment_code)}
                        </bdi>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 text-center">
                <p className="text-slate-600 dark:text-slate-400">
                  قسط پرداخت‌نشده‌ای تا امروز ندارید.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
